import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { acceptInvitation } from '../lib/invitations';
import { Building2, CheckCircle2, AlertCircle, Loader2, Users } from 'lucide-react';

const InvitationAcceptPage = ({ user, onAccepted }) => {
  const [token, setToken] = useState(null);
  const [invitationData, setInvitationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenValue = params.get('token') || params.get('t');
    setToken(tokenValue);

    if (!tokenValue) {
      setError('Token d\'invitation manquant dans l\'URL');
      setLoading(false);
      return;
    }

    console.log('Token d\'invitation détecté:', tokenValue);

    const fetchInvitation = async () => {
      try {
        console.log('Recherche de l\'invitation dans Firestore...');
        const invRef = doc(db, 'invitations', tokenValue);
        const invSnap = await getDoc(invRef);
        
        if (!invSnap.exists()) {
          console.error('Invitation non trouvée dans Firestore. Token:', tokenValue);
          setError(`Invitation non trouvée ou expirée. Token: ${tokenValue.substring(0, 8)}...`);
          setLoading(false);
          return;
        }

        console.log('Invitation trouvée:', invSnap.data());

        const data = invSnap.data();
        
        // Vérifier que l'email correspond
        if (data.email && user.email && data.email.toLowerCase() !== user.email.toLowerCase()) {
          setError(`Cette invitation est destinée à ${data.email}, mais vous êtes connecté avec ${user.email}`);
          setLoading(false);
          return;
        }

        setInvitationData({ id: tokenValue, ...data });
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors de la lecture de l\'invitation:', err);
        setError('Erreur lors du chargement de l\'invitation');
        setLoading(false);
      }
    };

    if (user) fetchInvitation();
  }, [user]);

  const handleAccept = async () => {
    setAccepting(true);
    setError('');
    try {
      console.log('Accepting invitation with token:', token);
      const result = await acceptInvitation(token);
      console.log('Accept result:', result);
      if (result && result.companyId) {
        setSuccess(true);
        // Appeler le callback et recharger la page après 3 secondes pour laisser Firestore propager
        setTimeout(() => {
          if (onAccepted) onAccepted(result.companyId);
          // Supprimer le token de l'URL et recharger
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
          window.location.reload();
        }, 3000);
      } else {
        setError('Erreur lors de l\'acceptation de l\'invitation');
      }
    } catch (err) {
      console.error('Erreur acceptation:', err);
      setError('Erreur lors de l\'acceptation. Vérifiez que vous êtes connecté avec le bon email.');
    } finally {
      setAccepting(false);
    }
  };

  const handleReject = () => {
    const newUrl = window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);
    window.location.href = '/';
  };

  const getRoleLabel = (role) => {
    const roles = {
      'member': { label: 'Membre', desc: 'Lecture/Écriture sur les chantiers', color: 'blue' },
      'editor': { label: 'Chef d\'équipe', desc: 'Créer, modifier et envoyer devis/factures', color: 'amber' },
      'admin': { label: 'Admin', desc: 'Accès complet', color: 'purple' }
    };
    return roles[role] || { label: role, desc: '', color: 'slate' };
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Loader2 size={48} className="animate-spin text-blue-600 mb-4" />
        <p className="text-slate-600 font-medium">Chargement de l'invitation...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-gradient-to-br from-red-50 to-orange-100 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle size={48} className="text-red-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-red-900 mb-2">Erreur</h1>
          <p className="text-red-700 mb-6">{error}</p>
          <button onClick={() => window.location.href = '/'} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-xl font-bold transition">
            Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle2 size={48} className="text-green-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-green-900 mb-2">Bienvenue ! ✨</h1>
          <p className="text-green-700 mb-1">Vous avez rejoint</p>
          <p className="text-xl font-bold text-slate-800 mb-6">{invitationData?.companyName}</p>
          <p className="text-sm text-slate-500 mb-6">Redirection en cours...</p>
          <Loader2 size={24} className="animate-spin text-green-600 mx-auto" />
        </div>
      </div>
    );
  }

  if (!invitationData) {
    return null;
  }

  const roleInfo = getRoleLabel(invitationData.role);
  const colorMap = {
    blue: 'from-blue-50 to-blue-100 border-blue-200',
    amber: 'from-amber-50 to-amber-100 border-amber-200',
    purple: 'from-purple-50 to-purple-100 border-purple-200',
    slate: 'from-slate-50 to-slate-100 border-slate-200'
  };

  return (
    <div className="h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl w-full">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center">
              <Building2 size={32} className="text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Vous êtes invité ! 🎉</h1>
          <p className="text-slate-600">Rejoignez l'équipe de votre entreprise</p>
        </div>

        <div className={`bg-gradient-to-br ${colorMap[roleInfo.color]} border-2 rounded-2xl p-6 mb-8`}>
          <div className="flex items-start gap-4">
            <Building2 size={32} className="text-slate-700 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Entreprise</p>
              <p className="text-2xl font-bold text-slate-900 mb-3">{invitationData.companyName}</p>
              
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Votre rôle</p>
              <div className="flex items-start gap-3">
                <Users size={20} className="text-slate-700 mt-0.5" />
                <div>
                  <p className="font-bold text-slate-900">{roleInfo.label}</p>
                  <p className="text-sm text-slate-600">{roleInfo.desc}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-50 rounded-xl p-4 mb-8 border border-slate-200">
          <p className="text-sm text-slate-700">
            <strong>Vous êtes connecté comme :</strong> <span className="text-blue-600 font-medium">{user?.email}</span>
          </p>
        </div>

        <div className="flex gap-4">
          <button 
            onClick={handleReject}
            disabled={accepting}
            className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition disabled:opacity-50"
          >
            Refuser
          </button>
          <button 
            onClick={handleAccept}
            disabled={accepting}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold rounded-xl hover:shadow-lg transition transform active:scale-95 flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {accepting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Acceptation...
              </>
            ) : (
              <>
                <CheckCircle2 size={18} />
                Accepter l'invitation
              </>
            )}
          </button>
        </div>

        <p className="text-xs text-slate-500 text-center mt-6">
          En acceptant cette invitation, vous rejoignez {invitationData.companyName} et accédez à tous les outils correspondant à votre rôle.
        </p>
      </div>
    </div>
  );
};

export default InvitationAcceptPage;
